import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../hooks/useToast';
import { adminService } from '../../services/admin';
import {
  IndividualMemberDto,
  VignetteStatus,
  getMemberDisplayFirstName,
  getMemberDisplayLastName,
  PagedResponse,
} from '../../types/member';
import { resolveVignetteStatus } from '../../utils/vignette';
import { VignetteStatusBadge } from '../../components/admin/VignetteStatusBadge';
import { AdminVignettePanel } from '../../components/admin/AdminVignettePanel';
import Modal from '../../components/common/Modal';
import SearchBar from '../../components/common/SearchBar';
import { DataTable, Column } from '../../components/common/DataTable';
import { Container, Stack } from '../../design-system/components/Layout';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Text } from '../../design-system/components/Typography';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';

const Page = styled(Container)`
  max-width: 1400px;
`;

const FiltersSection = styled(Card)`
  margin-bottom: ${spacing[6]};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(200px, 260px);
  gap: ${spacing[4]};
  align-items: end;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: ${spacing[2]};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;

const SelectStyled = styled.select`
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeOut
  )};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.focus(props.theme.colors.primary)};
  }
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const MemberName = styled.span`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

const MemberEmail = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
`;

const ModalBody = styled.div`
  min-width: min(100%, 560px);
`;

const PAGE_SIZE = 25;

const AdminVignettes: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { success: showSuccess, error: showError } = useToast();
  const [members, setMembers] = useState<IndividualMemberDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [yearFilter, setYearFilter] = useState<string>('');
  const [editingMember, setEditingMember] = useState<IndividualMemberDto | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response: PagedResponse<IndividualMemberDto> = await adminService.getMembers({
          page,
          pageSize: PAGE_SIZE,
          search: debouncedSearch || undefined,
          vignetteStatus: statusFilter === '' ? undefined : parseInt(statusFilter, 10),
          vignetteYear: yearFilter === '' ? undefined : parseInt(yearFilter, 10),
        });
        if (!cancelled) {
          setMembers(response.items);
          setTotal(response.total);
        }
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : t('common.error'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, statusFilter, yearFilter, t, showError]);

  const formatUpdatedAt = (value?: string) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(
      i18n.language.startsWith('el') || i18n.language === 'gr' ? 'el-GR' : 'en-GB',
      { dateStyle: 'medium' }
    );
  };

  const handleUpdated = (updated: IndividualMemberDto) => {
    setMembers(prev => prev.map(m => (m.id === updated.id ? { ...m, ...updated } : m)));
    setEditingMember(null);
  };

  const columns: Column<IndividualMemberDto>[] = [
    {
      key: 'name',
      header: t('admin.name'),
      render: member => (
        <NameCell>
          <MemberName>
            {getMemberDisplayFirstName(member)} {getMemberDisplayLastName(member)}
          </MemberName>
          <MemberEmail>{member.email}</MemberEmail>
        </NameCell>
      ),
    },
    {
      key: 'status',
      header: t('vignette.statusLabel'),
      render: member => <VignetteStatusBadge status={resolveVignetteStatus(member)} />,
    },
    {
      key: 'tracking',
      header: t('vignette.trackingNumberLabel'),
      render: member => (
        <Text size="sm" color="text">
          {resolveVignetteStatus(member) === VignetteStatus.SentByMail
            ? member.vignetteTrackingNumber?.trim() || '—'
            : '—'}
        </Text>
      ),
    },
    {
      key: 'updated',
      header: t('vignette.lastUpdated'),
      render: member => (
        <Text size="sm" color="secondary">
          {formatUpdatedAt(member.vignetteUpdatedAt)}
        </Text>
      ),
    },
    {
      key: 'actions',
      header: t('admin.actions'),
      actions: member => (
        <Button
          variant="outline"
          size="sm"
          onClick={e => {
            e.stopPropagation();
            setEditingMember(member);
          }}
        >
          <PencilSquareIcon style={{ width: 16, height: 16, marginRight: 6 }} />
          {t('vignette.management.edit')}
        </Button>
      ),
    },
  ];

  return (
    <Page>
      <PageHeader
        title={t('vignette.management.listTitle')}
        subtitle={t('vignette.management.listSubtitle')}
        pattern
      />

      <FiltersSection>
        <Stack direction="column" spacing="md">
          <FiltersGrid>
            <div>
              <FilterLabel>{t('admin.search')}</FilterLabel>
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder={t('vignette.management.searchPlaceholder')}
                fullWidth
              />
            </div>
            <div>
              <FilterLabel htmlFor="admin-vignettes-status">{t('vignette.statusLabel')}</FilterLabel>
              <SelectStyled
                id="admin-vignettes-status"
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('admin.all')}</option>
                <option value={String(VignetteStatus.NotSent)}>{t('vignette.notSent')}</option>
                <option value={String(VignetteStatus.SentByMail)}>{t('vignette.sentByMail')}</option>
                <option value={String(VignetteStatus.ReceivedInPerson)}>
                  {t('vignette.receivedInPerson')}
                </option>
              </SelectStyled>
            </div>
            <div>
              <FilterLabel htmlFor="admin-vignettes-year">{t('vignette.management.year')}</FilterLabel>
              <SelectStyled
                id="admin-vignettes-year"
                value={yearFilter}
                onChange={e => {
                  setYearFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('admin.all')}</option>
                {Array.from({ length: 12 }, (_, index) => new Date().getFullYear() - index).map(year => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </SelectStyled>
            </div>
          </FiltersGrid>
        </Stack>
      </FiltersSection>

      <DataTable
        columns={columns}
        data={members}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        emptyMessage={t('vignette.management.noMembers')}
      />

      <Modal
        isOpen={!!editingMember}
        onClose={() => setEditingMember(null)}
        title={
          editingMember
            ? `${getMemberDisplayFirstName(editingMember)} ${getMemberDisplayLastName(editingMember)}`
            : t('vignette.management.title')
        }
      >
        {editingMember && (
          <ModalBody>
            <AdminVignettePanel
              embedded
              memberId={editingMember.id}
              member={editingMember}
              onUpdated={handleUpdated}
              onError={showError}
              onSuccess={showSuccess}
            />
          </ModalBody>
        )}
      </Modal>
    </Page>
  );
};

export default AdminVignettes;
